// First Created - 15 Sep 2026
// Author - Atul Pandey
// Purpose - RTK Query API slice for Trinity Intention Board
//           candidate communications.

import {
  createSelector,
  createEntityAdapter,
} from '@reduxjs/toolkit'

import {
  apiSlice,
} from '@/app/api/apiSlice'


/*
|--------------------------------------------------------------------------
| Entity Adapter
|--------------------------------------------------------------------------
*/

const orgIntentionCandidateCommunicationsAdapter =
  createEntityAdapter({})


const initialState =
  orgIntentionCandidateCommunicationsAdapter
    .getInitialState()


/*
|--------------------------------------------------------------------------
| API Slice
|--------------------------------------------------------------------------
*/

export const orgIntentionCandidateCommunicationsApiSlice =
  apiSlice.injectEndpoints({

    endpoints:
      builder => ({

        /*
        |--------------------------------------------------------------------------
        | Get Board Candidate Communication Status - 24 Sep 2026
        |--------------------------------------------------------------------------
        |
        | Read only.
        |
        | Returns Initiation communication milestone state for all confirmed Go
        | candidates on one Intention Board.
        |
        */

        getOrgIntentionBoardCandidateCommunicationStatus:
          builder.query({

            query:
              ({
                orgId,
                intentionBoardId,
              }) => ({

                url:
                  '/orgIntentionCandidateCommunication/status',

                params: {

                  orgId,

                  intentionBoardId,

                },

                validateStatus:
                  (
                    response,
                    result
                  ) => {

                    return (
                      response.status ===
                        200 &&
                      !result.isError
                    )

                  },

              }),


            /*
            |--------------------------------------------------------------------------
            | Tags
            |--------------------------------------------------------------------------
            */

            providesTags:
              (
                result,
                error,
                arg
              ) => [

                {
                  type:
                    'OrgIntentionCandidateCommunication',

                  id:
                    `BOARD-STATUS-${arg?.intentionBoardId}`,
                },

              ],

          }),

        /*
        |--------------------------------------------------------------------------
        | Get Candidate Communication History
        |--------------------------------------------------------------------------
        |
        | Private
        |
        | Returns all active communications for one candidate.
        |
        */

        getOrgIntentionCandidateCommunications:
          builder.query({

            query:
              ({
                orgId,
                intentionBoardId,
                intentionCandidateId,
              }) => ({

                url:
                  '/orgIntentionCandidateCommunication',

                params: {

                  orgId,

                  intentionBoardId,

                  intentionCandidateId,

                },

                validateStatus:
                  (
                    response,
                    result
                  ) => {

                    return (
                      response.status ===
                        200 &&
                      !result.isError
                    )

                  },

              }),


            /*
            |--------------------------------------------------------------------------
            | Normalize Communication History
            |--------------------------------------------------------------------------
            |
            | Backend returns:
            |
            | {
            |   count,
            |   communications
            | }
            |
            */

            transformResponse:
              responseData => {

                const loadedCommunications =
                  (
                    responseData
                      ?.communications ||
                    []
                  )
                    .map(
                      communication => {

                        communication.id =
                          communication._id

                        return communication

                      }
                    )


                return {
                  count:
                    responseData
                      ?.count ||
                    loadedCommunications
                      .length,

                  communications:
                    orgIntentionCandidateCommunicationsAdapter
                      .setAll(
                        initialState,
                        loadedCommunications
                      ),
                }

              },


            /*
            |--------------------------------------------------------------------------
            | Tags
            |--------------------------------------------------------------------------
            */

            providesTags:
              (
                result,
                error,
                arg
              ) => {

                const communicationData =
                  result
                    ?.communications


                if (
                  communicationData
                    ?.ids
                ) {

                  return [

                    {
                      type:
                        'OrgIntentionCandidateCommunication',

                      id:
                        `CANDIDATE-${arg?.intentionCandidateId}`,
                    },

                    ...communicationData
                      .ids
                      .map(
                        id => ({

                          type:
                            'OrgIntentionCandidateCommunication',

                          id,

                        })
                      ),

                  ]

                }


                return [

                  {
                    type:
                      'OrgIntentionCandidateCommunication',

                    id:
                      `CANDIDATE-${arg?.intentionCandidateId}`,
                  },

                ]

              },

          }),


        /*
        |--------------------------------------------------------------------------
        | Preview Candidate Communication
        |--------------------------------------------------------------------------
        |
        | Private
        |
        | EtAdmin only.
        |
        | Builds the communication and returns:
        |
        | {
        |   preview,
        |   communicationType,
        |   recipient,
        |   ccRecipients,
        |   subject,
        |   text,
        |   html,
        |   metadata
        | }
        |
        | Does NOT:
        |
        | - persist communication history
        | - send email
        |
        */

        previewOrgIntentionCandidateCommunication:
          builder.mutation({

            query:
              initialCommunication => ({

                url:
                  '/orgIntentionCandidateCommunication/preview',

                method:
                  'POST',

                body: {
                  ...initialCommunication,
                },


                validateStatus:
                  (
                    response,
                    result
                  ) => {

                    return (
                      response.status ===
                        200 &&
                      !result.isError
                    )

                  },

              }),

          }),


        /*
        |--------------------------------------------------------------------------
        | Send Candidate Communication
        |--------------------------------------------------------------------------
        |
        | Private
        |
        | EtAdmin only.
        |
        | Persists and sends one candidate communication.
        |
        */

        sendOrgIntentionCandidateCommunication:
          builder.mutation({

            query:
              initialCommunication => ({

                url:
                  '/orgIntentionCandidateCommunication/send',

                method:
                  'POST',

                body: {
                  ...initialCommunication,
                },


                validateStatus:
                  (
                    response,
                    result
                  ) => {

                    return (
                      response.status ===
                        201 &&
                      !result.isError
                    )

                  },

              }),


            /*
            |--------------------------------------------------------------------------
            | Invalidate Communication History
            |--------------------------------------------------------------------------
            */

            invalidatesTags:
            (
              result,
              error,
              arg
            ) => {

              const candidateIds =
                arg
                  ?.intentionCandidateIds ||
                []


              return [

                /*
                |--------------------------------------------------------------------------
                | Board Communication Status
                |--------------------------------------------------------------------------
                */

                {
                  type:
                    'OrgIntentionCandidateCommunication',

                  id:
                    `BOARD-STATUS-${arg?.intentionBoardId}`,
                },


                /*
                |--------------------------------------------------------------------------
                | Candidate Communication Histories
                |--------------------------------------------------------------------------
                */

                ...candidateIds
                  .filter(
                    Boolean
                  )
                  .map(
                    candidateId => ({

                      type:
                        'OrgIntentionCandidateCommunication',

                      id:
                        `CANDIDATE-${candidateId}`,

                    })
                  ),

              ]

            },

          }),

      }),

  })


/*
|--------------------------------------------------------------------------
| Hooks
|--------------------------------------------------------------------------
*/

export const {

  // 24 Sep 2026
  useGetOrgIntentionBoardCandidateCommunicationStatusQuery,

  useGetOrgIntentionCandidateCommunicationsQuery,

  useLazyGetOrgIntentionCandidateCommunicationsQuery,

  usePreviewOrgIntentionCandidateCommunicationMutation,

  useSendOrgIntentionCandidateCommunicationMutation,

} =
  orgIntentionCandidateCommunicationsApiSlice


/*
|--------------------------------------------------------------------------
| Candidate Communication Query Result Selector
|--------------------------------------------------------------------------
*/

export const selectOrgIntentionCandidateCommunicationsResult =
  ({
    orgId,
    intentionBoardId,
    intentionCandidateId,
  }) =>
    orgIntentionCandidateCommunicationsApiSlice
      .endpoints
      .getOrgIntentionCandidateCommunications
      .select({

        orgId,

        intentionBoardId,

        intentionCandidateId,

      })


/*
|--------------------------------------------------------------------------
| Candidate Communication Data Selector
|--------------------------------------------------------------------------
*/

export const selectOrgIntentionCandidateCommunicationsData =
  ({
    orgId,
    intentionBoardId,
    intentionCandidateId,
  }) =>
    createSelector(

      selectOrgIntentionCandidateCommunicationsResult({
        orgId,
        intentionBoardId,
        intentionCandidateId,
      }),

      communicationsResult =>
        communicationsResult
          .data
          ?.communications

    )


/*
|--------------------------------------------------------------------------
| Communication Entity Selectors
|--------------------------------------------------------------------------
*/

export const selectOrgIntentionCandidateCommunicationSelectors =
  ({
    orgId,
    intentionBoardId,
    intentionCandidateId,
  }) =>
    orgIntentionCandidateCommunicationsAdapter
      .getSelectors(

        state => {

          const communicationData =
            selectOrgIntentionCandidateCommunicationsData({
              orgId,
              intentionBoardId,
              intentionCandidateId,
            })(
              state
            )


          return (
            communicationData ??
            initialState
          )

        }

      )